import { Card, Grid, Divider, TextField, Tabs, Tab } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Loading, Notify } from 'notiflix';
import * as yup from 'yup';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';
import usePermission from 'hooks/usePermission';

// Admin → Settings → Invoice Settings. Everything here used to be hardcoded in
// the invoice (web modal, downloaded PDF and the PDF attached to emails).
// Fields always show the value the invoice currently uses. Clearing a field
// and saving restores its default (the backend treats empty as "default").

const REGIONS = [
  { key: 'AU', label: 'Australia', hint: 'Shown in "From" on invoices billed to Australia.' },
  { key: 'IN', label: 'India', hint: 'Shown in "From" on invoices billed to India.' },
  { key: 'DEFAULT', label: 'Other countries', hint: 'Shown in "From" on invoices billed to any other country.' },
];

const SELLER_FIELDS = [
  { name: 'legalName', label: 'Company legal name' },
  { name: 'address', label: 'Company address', multiline: true },
  { name: 'taxIdLabel', label: 'Tax ID label', helper: 'e.g. ABN, GSTIN' },
  { name: 'taxId', label: 'Tax ID number' },
  { name: 'cin', label: 'CIN', helper: 'Company Identification Number — India only, leave empty elsewhere' },
  { name: 'phone', label: 'Phone' },
  { name: 'email', label: 'Email' },
  { name: 'website', label: 'Website', helper: 'Shown as plain text, e.g. www.desteliq.com' },
];

const TEXT_FIELDS = [
  {
    name: 'footerLinkLabel',
    label: 'Footer link text',
    helper: 'Clickable text in the invoice footer',
  },
  {
    name: 'footerLinkPath',
    label: 'Footer link target',
    helper: 'A site page like /legal/privacy-policy, or a full https:// URL',
  },
  { name: 'thanksText', label: 'Footer sign-off', helper: 'Line under the footer link' },
  {
    name: 'taxNoteAU',
    label: 'Tax line — Australia',
    helper: 'Bottom-left of AU invoices. {currency} becomes the currency (e.g. INR) and {rate} the tax rate with % (e.g. 18%).',
  },
  {
    name: 'taxNoteIN',
    label: 'Tax line — India',
    helper: 'Bottom-left of India invoices. {currency} becomes the currency (e.g. INR) and {rate} the tax rate with % (e.g. 18%).',
  },
  {
    name: 'indiaEInvoiceNote',
    label: 'India e-invoice note',
    helper: 'Shown under the tax line on India invoices',
    multiline: true,
  },
];

const EMPTY_SELLER = Object.fromEntries(SELLER_FIELDS.map((f) => [f.name, '']));
const EMPTY_FORM = {
  sellers: { AU: { ...EMPTY_SELLER }, IN: { ...EMPTY_SELLER }, DEFAULT: { ...EMPTY_SELLER } },
  ...Object.fromEntries(TEXT_FIELDS.map((f) => [f.name, ''])),
};

const schema = yup.object().shape({
  footerLinkPath: yup
    .string()
    .test(
      'path-or-url',
      'Must start with / (a site page) or http(s):// (a full URL)',
      (value) => !value || /^(\/|https?:\/\/)/i.test(value.trim()),
    ),
});

// The effective settings from the API (saved values, defaults filling any gap)
// → form values, so every field shows exactly what the invoice prints.
const toFormValues = (settings) => {
  const values = structuredClone(EMPTY_FORM);
  for (const { key } of REGIONS) {
    for (const { name } of SELLER_FIELDS) values.sellers[key][name] = settings?.sellers?.[key]?.[name] || '';
  }
  for (const { name } of TEXT_FIELDS) values[name] = settings?.[name] || '';
  return values;
};

const InvoiceSettings = () => {
  const { user } = useSelector((state) => state.auth);
  const canUpdate = usePermission('settings', 'update');
  const [tab, setTab] = useState(0);
  const [defaults, setDefaults] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${user?.accessToken}` } };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm({ resolver: yupResolver(schema), defaultValues: EMPTY_FORM });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      Loading.circle('Loading invoice settings...');
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/invoice-settings`, authHeader);
      setDefaults(data.data.defaults);
      reset(toFormValues(data.data.settings));
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to fetch invoice settings');
    } finally {
      Loading.remove();
    }
  };

  const handleSave = async (formValues) => {
    try {
      Loading.circle('Saving...');
      const { data } = await axios.put(`${ENDPOINT}api/v1/admin/invoice-settings`, formValues, authHeader);
      reset(toFormValues(data.data.settings));
      Notify.success('Invoice settings saved — new invoices and invoice emails will use them');
    } catch (error) {
      console.error(error);
      Notify.failure(error.response?.data?.message || 'Failed to save invoice settings');
    } finally {
      Loading.remove();
    }
  };

  const renderField = (name, { label, helper, multiline }, placeholder) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          id={name}
          value={field.value || ''}
          label={label}
          placeholder={placeholder || ''}
          fullWidth
          margin="normal"
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          InputLabelProps={{ shrink: true }}
          error={!!fieldState.error}
          helperText={
            fieldState.error?.message ||
            [helper, placeholder ? `Clear and save to restore the default: ${placeholder}` : '']
              .filter(Boolean)
              .join(' · ')
          }
        />
      )}
    />
  );

  const region = REGIONS[tab];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={9}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h5" mb={1}>
                Invoice Settings
              </MDTypography>
              <MDTypography variant="caption" color="secondary">
                Company details, footer link and notes printed on every tax invoice — the web invoice,
                the downloaded PDF and the PDF attached to order emails. Changes apply to invoices
                generated after saving.
              </MDTypography>
              <Divider sx={{ my: 2 }} />

              <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 1 }}>
                {REGIONS.map((r) => (
                  <Tab key={r.key} label={r.label} />
                ))}
                <Tab label="Footer & notes" />
              </Tabs>

              <form onSubmit={handleSubmit(handleSave)}>
                {region ? (
                  <MDBox>
                    <MDTypography variant="caption" color="secondary" display="block" mt={1}>
                      {region.hint}
                    </MDTypography>
                    {SELLER_FIELDS.map((f) =>
                      renderField(`sellers.${region.key}.${f.name}`, f, defaults?.sellers?.[region.key]?.[f.name]),
                    )}
                  </MDBox>
                ) : (
                  <MDBox>{TEXT_FIELDS.map((f) => renderField(f.name, f, defaults?.[f.name]))}</MDBox>
                )}

                <MDBox mt={3} display="flex" gap={2} alignItems="center">
                  {canUpdate && (
                    <MDButton type="submit" variant="gradient" color="info" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving...' : 'Save Invoice Settings'}
                    </MDButton>
                  )}
                  {isDirty && (
                    <MDTypography variant="caption" color="warning">
                      Unsaved changes (saving applies all tabs)
                    </MDTypography>
                  )}
                </MDBox>
              </form>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default InvoiceSettings;
